'use client';

import classNames from 'classnames';
import {gql} from "@apollo/client";
import PropTypes from "prop-types";
import PostAuthor from "@/components/PostAuthor/PostAuthor";
import dynamic from "next/dynamic";
import TermLinks from "@/components/TermLinks/TermLinks";
import GoogleButtons from "@/components/GoogleButtons/GoogleButtons";
import { format, parseISO } from 'date-fns';

const PostActions = dynamic(() => import('@/components/PostActions/PostActions').then((mod) => mod.default));
const Comments = dynamic(() => import('@/components/Comments/Comments').then((mod) => mod.default));

export default function PostFooter({post, className}) {
  const {date, modified, tags, momPublicAuthors, momCommentsEnabled} = post;

  // Show original publish date footnote only when:
  // 1. Published within the last 18 months
  // 2. Published and modified dates are in different months
  let originalPublishDate = null;
  try {
    if (date) {
      const parsedDate = typeof date === 'string' ? parseISO(date) : new Date(date);
      const parsedModified = modified ? (typeof modified === 'string' ? parseISO(modified) : new Date(modified)) : null;
      const eighteenMonthsAgo = new Date();
      eighteenMonthsAgo.setMonth(eighteenMonthsAgo.getMonth() - 18);
      const sameMonth = parsedModified &&
        parsedDate.getFullYear() === parsedModified.getFullYear() &&
        parsedDate.getMonth() === parsedModified.getMonth();
      if (!isNaN(parsedDate.valueOf()) && parsedDate >= eighteenMonthsAgo && !sameMonth) {
        originalPublishDate = format(parsedDate, 'MMMM yyyy');
      }
    }
  } catch (e) {}

  return (
    <>
      {originalPublishDate && (
        <p className="italic text-sm text-muted-foreground mt-6 bg-muted px-4 py-3 rounded" suppressHydrationWarning>
          This article was originally published in {originalPublishDate}.
        </p>
      )}

      <div className={classNames(
        'grid grid-cols-1 md:grid-cols-10 gap-4',
        'mom-border border-t-2 py-6 md:py-6 mt-6 md:mt-8',
        className
      )}>
        <div className={classNames('md:col-span-10 mb-2 mom-border border-b-2 pb-6')}>
          <GoogleButtons />
        </div>

        <div className={classNames('md:col-span-5 text-left')}>
          <TermLinks terms={tags}/>
        </div>

        <PostActions className={`md:col-span-5 md:justify-end`} reverse={true} storyId={post.databaseId}/>
      </div>

      {momPublicAuthors?.map((author, index) => (
        <PostAuthor key={index} publicAuthor={author} showAboutLink={index === momPublicAuthors.length - 1}/>
      ))}

      {momCommentsEnabled && <Comments storyId={post.databaseId}/>}
    </>
  );
};

PostFooter.propTypes = {
  post: PropTypes.object.isRequired,
};

PostFooter.fragments = {
  key: 'PostFooterFragment',
  entry: gql`
    ${PostAuthor.fragments.entry}
    ${TermLinks.fragments.entryTag}
    fragment PostFooterFragment on Post {
      date
      modified
      momPublicAuthors {
        ...${PostAuthor.fragments.key}
      }
      tags {
        ...${TermLinks.fragments.keyTag}
      }
      momCommentsEnabled
    }
  `
};
